// GTM (with GA4), Meta Pixel, TikTok Pixel and PostHog load ONLY after cookie
// consent (Accept). Nothing tracking-related loads in index.html. Consent Mode
// v2 defaults to denied in index.html; consent is granted here on Accept.
// On native (Capacitor) builds, NO tracker ever initializes, regardless of
// consent, so the mobile app ships free of GA4/Meta/TikTok/PostHog.
import { isNative } from "@/lib/native";
let _pixelStarted = false;
let _posthogStarted = false;
let _gtmStarted = false;

const GTM_ID = "GTM-MTCVQDFL";

// Magic-login (/access/<token>) and post-checkout (/thank-you?token=...&email=...)
// URLs carry secrets/PII in the path or query. Never let them reach GA4, Meta or
// TikTok. We do NOT rewrite the address bar (that would break magic-link refresh);
// instead we suppress the URL-bearing PageView on these routes and feed GA4 a
// cleaned page_location. PostHog is already scrubbed in its sanitize_properties.
function _isSensitivePath() {
  try {
    const p = window.location.pathname || "";
    return p.indexOf("/access/") === 0 || p.indexOf("/thank-you") === 0;
  } catch (e) {
    return false;
  }
}
function _cleanLocation() {
  const p = window.location.pathname || "/";
  let clean = p;
  if (p.indexOf("/access/") === 0) clean = "/access";
  else if (p.indexOf("/thank-you") === 0) clean = "/thank-you";
  return { path: clean, url: window.location.origin + clean };
}

// Inject the GTM container. GA4 (G-Y3S27DT3ZQ) and any Google tags fire only
// through this container, which is loaded exclusively after consent Accept.
function loadGTM() {
  if (_gtmStarted || document.getElementById("gtm-loader")) return;
  _gtmStarted = true;
  /* eslint-disable */
  (function (w, d, s, l, i) {
    w[l] = w[l] || [];
    w[l].push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
    var f = d.getElementsByTagName(s)[0],
      j = d.createElement(s),
      dl = l != "dataLayer" ? "&l=" + l : "";
    j.id = "gtm-loader";
    j.async = true;
    j.src = "https://www.googletagmanager.com/gtm.js?id=" + i + dl;
    f.parentNode.insertBefore(j, f);
  })(window, document, "script", "dataLayer", GTM_ID);
  /* eslint-enable */
}

const POSTHOG_KEY = "phc_xAvL2Iq4tFmANRE7kzbKwaSqp1HJjN7x48s3vr0CMjs";
const POSTHOG_HOST = "https://us.i.posthog.com";

function startPostHog() {
  if (_posthogStarted) return;
  _posthogStarted = true;
  /* eslint-disable */
  !(function (t, e) {
    var o, n, p, r;
    e.__SV ||
      ((window.posthog = e),
      (e._i = []),
      (e.init = function (i, s, a) {
        function g(t, e) {
          var o = e.split(".");
          2 == o.length && ((t = t[o[0]]), (e = o[1])),
            (t[e] = function () {
              t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
            });
        }
        ((p = t.createElement("script")).type = "text/javascript"),
          (p.crossOrigin = "anonymous"),
          (p.async = !0),
          (p.src = s.api_host.replace(".i.posthog.com", "-assets.i.posthog.com") + "/static/array.js"),
          (r = t.getElementsByTagName("script")[0]).parentNode.insertBefore(p, r);
        var u = e;
        for (
          void 0 !== a ? (u = e[a] = []) : (a = "posthog"),
            u.people = u.people || [],
            u.toString = function (t) {
              var e = "posthog";
              return "posthog" !== a && (e += "." + a), t || (e += " (stub)"), e;
            },
            u.people.toString = function () {
              return u.toString(1) + ".people (stub)";
            },
            o = "init capture register register_once identify reset group set_config get_distinct_id opt_in_capturing opt_out_capturing startSessionRecording stopSessionRecording".split(" "),
            n = 0;
          n < o.length;
          n++
        )
          g(u, o[n]);
        e._i.push([i, s, a]);
      }),
      (e.__SV = 1));
  })(document, window.posthog || []);
  window.posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    person_profiles: "identified_only",
    session_recording: { recordCrossOriginIframes: true, capturePerformance: false },
    // Redact access tokens / magic-login tokens from captured URLs.
    sanitize_properties: function (props) {
      const scrub = function (v) {
        if (typeof v !== "string") return v;
        return v
          .replace(/\/access\/[^/?#]+/gi, "/access/[REDACTED]")
          .replace(/([?&](token|session_id|t)=)[^&#]+/gi, "$1[REDACTED]");
      };
      ["$current_url", "$pathname", "$referrer", "$initial_current_url"].forEach(function (k) {
        if (props[k]) props[k] = scrub(props[k]);
      });
      return props;
    },
  });
  /* eslint-enable */
}

function initMetaPixel() {
  const pixelId = process.env.REACT_APP_META_PIXEL_ID || "1730607941696390";
  if (!pixelId) return;
  /* eslint-disable */
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = "2.0";
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  /* eslint-enable */
  window.fbq("init", pixelId);
  // Suppress the URL-bearing PageView on token/PII routes (see _isSensitivePath).
  if (!_isSensitivePath()) window.fbq("track", "PageView");
}

function initTikTokPixel() {
  const pixelId = process.env.REACT_APP_TIKTOK_PIXEL_ID || "DA9SPA3C77U250DBK0H0";
  if (!pixelId || window.ttq?.loaded) return;
  /* eslint-disable */
  !(function (w, d, t) {
    w.TiktokAnalyticsObject = t;
    var ttq = (w[t] = w[t] || []);
    ttq.methods = ["page", "track", "identify", "instances", "debug", "on", "off", "once", "ready", "alias", "group", "enableCookie", "disableCookie", "holdConsent", "revokeConsent", "grantConsent"];
    ttq.setAndDefer = function (q, method) {
      q[method] = function () {
        q.push([method].concat(Array.prototype.slice.call(arguments, 0)));
      };
    };
    for (var i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(ttq, ttq.methods[i]);
    ttq.instance = function (id) {
      var instance = ttq._i[id] || [];
      for (var j = 0; j < ttq.methods.length; j++) ttq.setAndDefer(instance, ttq.methods[j]);
      return instance;
    };
    ttq.load = function (id, options) {
      var src = "https://analytics.tiktok.com/i18n/pixel/events.js";
      ttq._i = ttq._i || {};
      ttq._i[id] = [];
      ttq._i[id]._u = src;
      ttq._t = ttq._t || {};
      ttq._t[id] = +new Date();
      ttq._o = ttq._o || {};
      ttq._o[id] = options || {};
      var script = d.createElement("script");
      script.type = "text/javascript";
      script.async = true;
      script.src = src + "?sdkid=" + id + "&lib=" + t;
      var first = d.getElementsByTagName("script")[0];
      first.parentNode.insertBefore(script, first);
    };
    ttq.load(pixelId);
    ttq.grantConsent();
    // Suppress the URL-bearing page view on token/PII routes.
    if (!_isSensitivePath()) ttq.page();
    ttq.loaded = true;
  })(window, document, "ttq");
  /* eslint-enable */
}

export function initTracking() {
  if (isNative()) return; // never load third-party trackers inside the native app
  if (_pixelStarted) return;
  _pixelStarted = true;
  // On token/PII routes, hand GA4 a cleaned page_location before GTM loads so the
  // first (and only) page_view never carries the token or query string.
  if (_isSensitivePath() && typeof window.gtag === "function") {
    const c = _cleanLocation();
    window.gtag("set", { page_location: c.url, page_path: c.path, page_referrer: window.location.origin + "/" });
  }
  loadGTM();
  startPostHog();
  initMetaPixel();
  initTikTokPixel();
}

// Update Google Consent Mode when the user accepts/declines cookies.
export function setConsent(granted) {
  if (isNative()) return; // no consent signals on native; nothing is loaded there
  const value = granted ? "granted" : "denied";
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: "consent_update", consent_state: value });
  if (typeof window.gtag === "function") {
    window.gtag("consent", "update", {
      ad_storage: value,
      analytics_storage: value,
      ad_user_data: value,
      ad_personalization: value,
    });
  }
  if (window.ttq) {
    if (granted && typeof window.ttq.grantConsent === "function") window.ttq.grantConsent();
    if (!granted && typeof window.ttq.revokeConsent === "function") window.ttq.revokeConsent();
  }
}
